import { useId } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  /** Phosphor icon (or any ReactNode). Rendered inside a circular tinted bubble. */
  icon: ReactNode;
  /** Primary heading. Required. Renders as `<h2>` inside the region. */
  title: string;
  /** Supporting copy; 1–2 short sentences. */
  description?: string;
  /** CTA node (usually a <Button>). Optional. */
  action?: ReactNode;
  /** Optional className for layout composition. */
  className?: string;
}

/**
 * Empty-state primitive. Renders a `role="region"` with `aria-labelledby`
 * pointing at the `<h2>` title so screen readers announce the state coherently.
 *
 * Spec: docs/architecture/states-pattern.md §2.2.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): ReactNode {
  const headingId = useId();
  return (
    <section
      role="region"
      aria-labelledby={headingId}
      data-state="empty"
      className={cn(
        "flex min-h-[240px] w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border bg-card/40 p-8 text-center",
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"
      >
        {icon}
      </div>
      <div className="space-y-1">
        <h2 id={headingId} className="text-lg font-semibold text-foreground">
          {title}
        </h2>
        {description ? (
          <p className="mx-auto max-w-md text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="pt-2">{action}</div> : null}
    </section>
  );
}
